import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ServiceStatus {
  name: string;
  category: string;
  status: 'online' | 'offline' | 'unknown';
  lastChecked: Date | null;
  responseTime: number | null;
  message: string | null;
}

const REFRESH_INTERVAL = 30000; // 30 seconds
// Minimum interval between checks for paid services (5 minutes to avoid unnecessary costs)
const PAID_SERVICE_CHECK_INTERVAL = 300000; // 5 minutes in milliseconds

export default function AdminStatus() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  // Track last check time for paid services to avoid excessive API calls
  const [lastPaidServiceCheck, setLastPaidServiceCheck] = useState<Record<string, number>>({});
  // Ref to prevent multiple simultaneous checks
  const isCheckingRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkSupabaseDatabase = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      const { error } = await supabase.from('locations').select('id').limit(1);
      const responseTime = Date.now() - startTime;
      
      return {
        name: 'Database',
        category: 'Supabase',
        status: error ? 'offline' : 'online',
        lastChecked: new Date(),
        responseTime,
        message: error ? error.message : null,
      };
    } catch (error) {
      return {
        name: 'Database',
        category: 'Supabase',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkSupabaseAuth = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      const { error } = await supabase.auth.getSession();
      const responseTime = Date.now() - startTime;
      
      return {
        name: 'Auth',
        category: 'Supabase',
        status: error ? 'offline' : 'online',
        lastChecked: new Date(),
        responseTime,
        message: error ? error.message : null,
      };
    } catch (error) {
      return {
        name: 'Auth',
        category: 'Supabase',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkSupabaseStorage = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      const { data, error } = await supabase.storage.listBuckets();
      const responseTime = Date.now() - startTime;
      
      return {
        name: 'Storage',
        category: 'Supabase',
        status: error ? 'offline' : 'online',
        lastChecked: new Date(),
        responseTime,
        message: error ? error.message : data ? `${data.length} buckets found` : null,
      };
    } catch (error) {
      return {
        name: 'Storage',
        category: 'Supabase',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkSupabaseEdgeFunctions = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      // Get URL and key from environment variables (same approach as functions.ts)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      
      if (!supabaseUrl || !anonKey) {
        return {
          name: 'Edge Functions',
          category: 'Supabase',
          status: 'unknown',
          lastChecked: new Date(),
          responseTime: null,
          message: 'Environment variables not configured',
        };
      }

      // Call health-check Edge Function for general Edge Functions status
      // Use supabase.functions.invoke to ensure proper headers are set
      const { data, error } = await supabase.functions.invoke('health-check', {
        body: {},
      });
      
      const responseTime = Date.now() - startTime;
      
      // If health check succeeds, the service is online
      const isOnline = !error && data?.status === 'ok';
      
      return {
        name: 'Edge Functions',
        category: 'Supabase',
        status: isOnline ? 'online' : 'offline',
        lastChecked: new Date(),
        responseTime,
        message: isOnline ? (data?.status === 'ok' ? 'Service operational' : null) : (error?.message || 'Service unavailable'),
      };
    } catch (error) {
      return {
        name: 'Edge Functions',
        category: 'Supabase',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkSupabaseRealtime = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      // Check if Realtime endpoint is accessible
      // Since we can't open WebSocket here, we check if Supabase client has realtime configured
      const responseTime = Date.now() - startTime;
      
      return {
        name: 'Realtime',
        category: 'Supabase',
        status: supabase.realtime ? 'online' : 'unknown',
        lastChecked: new Date(),
        responseTime,
        message: supabase.realtime ? 'Realtime client available' : 'Realtime not configured',
      };
    } catch (error) {
      return {
        name: 'Realtime',
        category: 'Supabase',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkStripe = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      // Simple health check on Stripe API
      const response = await fetch('https://api.stripe.com/v1/charges?limit=1', {
        method: 'GET',
        headers: {
          'Authorization': 'Bearer sk_test_dummy', // Dummy key just to verify if API responds
        },
      });
      
      const responseTime = Date.now() - startTime;
      
      // Stripe returns 401 if key is invalid, but this means API is online
      const isOnline = response.status === 401 || response.ok;
      
      return {
        name: 'Stripe API',
        category: 'Payments',
        status: isOnline ? 'online' : 'offline',
        lastChecked: new Date(),
        responseTime,
        message: isOnline ? null : `HTTP ${response.status}`,
      };
    } catch (error) {
      return {
        name: 'Stripe API',
        category: 'Payments',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkVonage = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      // Check if Vonage SMS is configured via centralized health check Edge Function
      // This avoids calling send-sms which would generate errors in console
      const { data, error } = await supabase.functions.invoke('health-check', {
        body: { service: 'vonage' },
      });
      
      const responseTime = Date.now() - startTime;
      
      // If health check succeeds, the service is online
      const isOnline = !error && data?.status === 'ok';
      
      return {
        name: 'Vonage SMS',
        category: 'Communications',
        status: isOnline ? 'online' : 'offline',
        lastChecked: new Date(),
        responseTime,
        message: isOnline ? (data?.configured ? 'Vonage SMS configured and available' : 'Service accessible') : (error?.message || 'Service unavailable'),
      };
    } catch (error) {
      return {
        name: 'Vonage SMS',
        category: 'Communications',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkPostmark = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      // Check if Postmark Email is configured via centralized health check Edge Function
      // This avoids calling send-email which would generate errors in console
      const { data, error } = await supabase.functions.invoke('health-check', {
        body: { service: 'postmark' },
      });
      
      const responseTime = Date.now() - startTime;
      
      // If health check succeeds and status is ok, the service is online
      // If error in response or status is error, service is offline/not configured
      const isOnline = !error && data?.status === 'ok' && data?.configured === true;
      
      let message = 'Service accessible';
      if (data?.status === 'error' || data?.configured === false) {
        message = data?.error || 'Postmark API key not configured';
      } else if (isOnline) {
        message = 'Postmark Email configured and available';
      } else if (error) {
        message = error.message || 'Service unavailable';
      }
      
      return {
        name: 'Postmark Email',
        category: 'Communications',
        status: isOnline ? 'online' : 'offline',
        lastChecked: new Date(),
        responseTime,
        message: message,
      };
    } catch (error) {
      return {
        name: 'Postmark Email',
        category: 'Communications',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  const checkGoogleMaps = async (): Promise<ServiceStatus> => {
    const startTime = Date.now();
    try {
      // Check if Google Maps is loaded
      const isLoaded = typeof window !== 'undefined' && 
                      typeof window.google !== 'undefined' && 
                      typeof window.google.maps !== 'undefined';
      
      const responseTime = Date.now() - startTime;
      
      return {
        name: 'Google Maps',
        category: 'Location Services',
        status: isLoaded ? 'online' : 'unknown',
        lastChecked: new Date(),
        responseTime,
        message: isLoaded ? 'Google Maps API loaded' : 'Google Maps API not loaded',
      };
    } catch (error) {
      return {
        name: 'Google Maps',
        category: 'Location Services',
        status: 'offline',
        lastChecked: new Date(),
        responseTime: Date.now() - startTime,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  // Helper to check if enough time has passed since last paid service check
  const shouldCheckPaidService = (serviceName: string): boolean => {
    const lastCheck = lastPaidServiceCheck[serviceName] || 0;
    const timeSinceLastCheck = Date.now() - lastCheck;
    return timeSinceLastCheck >= PAID_SERVICE_CHECK_INTERVAL;
  };

  // Update last check time for a paid service
  const updateLastPaidServiceCheck = (serviceName: string) => {
    setLastPaidServiceCheck(prev => ({
      ...prev,
      [serviceName]: Date.now(),
    }));
  };

  const checkAllServices = async (forcePaidServices = false) => {
    // Prevent multiple simultaneous checks
    if (isCheckingRef.current) {
      return;
    }
    
    isCheckingRef.current = true;
    setIsRefreshing(true);
    
    const now = Date.now();
    const checks: Promise<ServiceStatus>[] = [
      // Free services - always check
      checkSupabaseDatabase(),
      checkSupabaseAuth(),
      checkSupabaseStorage(),
      checkSupabaseRealtime(),
      checkGoogleMaps(),
    ];

    // Paid services - only check if interval has passed or forced
    if (forcePaidServices || shouldCheckPaidService('edge-functions')) {
      checks.push(checkSupabaseEdgeFunctions());
      updateLastPaidServiceCheck('edge-functions');
    } else {
      // Return cached status if available
      const cached = services.find(s => s.name === 'Edge Functions');
      if (cached) {
        checks.push(Promise.resolve({
          ...cached,
          message: cached.message ? `${cached.message} (cached - next check in ${Math.ceil((PAID_SERVICE_CHECK_INTERVAL - (now - (lastPaidServiceCheck['edge-functions'] || 0))) / 1000)}s)` : `Cached - next check in ${Math.ceil((PAID_SERVICE_CHECK_INTERVAL - (now - (lastPaidServiceCheck['edge-functions'] || 0))) / 1000)}s`,
        }));
      } else {
        checks.push(checkSupabaseEdgeFunctions());
        updateLastPaidServiceCheck('edge-functions');
      }
    }

    if (forcePaidServices || shouldCheckPaidService('stripe')) {
      checks.push(checkStripe());
      updateLastPaidServiceCheck('stripe');
    } else {
      const cached = services.find(s => s.name === 'Stripe API');
      if (cached) {
        checks.push(Promise.resolve({
          ...cached,
          message: cached.message ? `${cached.message} (cached)` : `Cached - next check in ${Math.ceil((PAID_SERVICE_CHECK_INTERVAL - (now - (lastPaidServiceCheck['stripe'] || 0))) / 1000)}s`,
        }));
      } else {
        checks.push(checkStripe());
        updateLastPaidServiceCheck('stripe');
      }
    }

    if (forcePaidServices || shouldCheckPaidService('vonage')) {
      checks.push(checkVonage());
      updateLastPaidServiceCheck('vonage');
    } else {
      const cached = services.find(s => s.name === 'Vonage SMS');
      if (cached) {
        checks.push(Promise.resolve({
          ...cached,
          message: cached.message ? `${cached.message} (cached)` : `Cached - next check in ${Math.ceil((PAID_SERVICE_CHECK_INTERVAL - (now - (lastPaidServiceCheck['vonage'] || 0))) / 1000)}s`,
        }));
      } else {
        checks.push(checkVonage());
        updateLastPaidServiceCheck('vonage');
      }
    }

    if (forcePaidServices || shouldCheckPaidService('postmark')) {
      checks.push(checkPostmark());
      updateLastPaidServiceCheck('postmark');
    } else {
      const cached = services.find(s => s.name === 'Postmark Email');
      if (cached) {
        checks.push(Promise.resolve({
          ...cached,
          message: cached.message ? `${cached.message} (cached)` : `Cached - next check in ${Math.ceil((PAID_SERVICE_CHECK_INTERVAL - (now - (lastPaidServiceCheck['postmark'] || 0))) / 1000)}s`,
        }));
      } else {
        checks.push(checkPostmark());
        updateLastPaidServiceCheck('postmark');
      }
    }

    const results = await Promise.all(checks);
    setServices(results);
    setLastRefresh(new Date());
    setIsRefreshing(false);
    isCheckingRef.current = false;
  };

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    let mounted = true;

    // Initial check (force paid services on first load)
    if (mounted) {
      checkAllServices(true);
    }

    // Setup auto-refresh (only free services, paid services check on longer interval)
    intervalRef.current = setInterval(() => {
      if (mounted && !isCheckingRef.current) {
        checkAllServices(false);
      }
    }, REFRESH_INTERVAL);

    return () => {
      mounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const getStatusBadge = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'online':
        return (
          <Badge className="bg-green-500 hover:bg-green-600 text-white">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Online
          </Badge>
        );
      case 'offline':
        return (
          <Badge className="bg-red-500 hover:bg-red-600 text-white">
            <XCircle className="h-3 w-3 mr-1" />
            Offline
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-500 hover:bg-yellow-600 text-white">
            <AlertCircle className="h-3 w-3 mr-1" />
            Unknown
          </Badge>
        );
    }
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'online':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'offline':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const onlineCount = services.filter(s => s.status === 'online').length;
  const offlineCount = services.filter(s => s.status === 'offline').length;
  const unknownCount = services.filter(s => s.status === 'unknown').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Service Status</h2>
          <p className="text-muted-foreground">
            Monitoring the status of all stack components
          </p>
        </div>
        <Button
          onClick={() => checkAllServices(true)}
          disabled={isRefreshing}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
          {isRefreshing ? 'Checking...' : 'Refresh All'}
        </Button>
      </div>

      {lastRefresh && (
        <p className="text-sm text-muted-foreground">
          Last checked: {lastRefresh.toLocaleTimeString('en-US')} • 
          Next automatic check in 30s
        </p>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Online</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{onlineCount}</div>
            <p className="text-xs text-muted-foreground">
              Operational services
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Offline</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{offlineCount}</div>
            <p className="text-xs text-muted-foreground">
              Unavailable services
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Unknown</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unknownCount}</div>
            <p className="text-xs text-muted-foreground">
              Unknown status
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Service Cards by Category */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {services.map((service) => (
          <Card key={`${service.category}-${service.name}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">{service.name}</CardTitle>
              {getStatusIcon(service.status)}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Category:</span>
                  <span className="text-xs font-medium">{service.category}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Status:</span>
                  {getStatusBadge(service.status)}
                </div>
                {service.responseTime !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Response time:</span>
                    <span className="text-xs font-medium">{service.responseTime}ms</span>
                  </div>
                )}
                {service.lastChecked && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Checked:</span>
                    <span className="text-xs font-medium">
                      {service.lastChecked.toLocaleTimeString('en-US')}
                    </span>
                  </div>
                )}
                {service.message && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">{service.message}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Service Details</CardTitle>
          <CardDescription>
            Detailed information about the status of each component
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Response Time</TableHead>
                <TableHead>Last Checked</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service) => (
                <TableRow key={`${service.category}-${service.name}`}>
                  <TableCell className="font-medium">{service.name}</TableCell>
                  <TableCell>{service.category}</TableCell>
                  <TableCell>{getStatusBadge(service.status)}</TableCell>
                  <TableCell>
                    {service.responseTime !== null ? (
                      <span className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-muted-foreground" />
                        {service.responseTime}ms
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {service.lastChecked ? (
                      service.lastChecked.toLocaleString('en-US')
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {service.message ? (
                      <span className="text-sm text-muted-foreground">{service.message}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

